import React from "react";
import moment from "moment";
import { ArrowRight, Clock, Copy, RefreshCw, Send } from "lucide-react";

import { kycLinkStyles as styles } from "./styles";
import { Callout, PrimaryButton } from "./parts";

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, HH:mm") : "—";
};

const formatExpiry = (value?: string) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM, HH:mm") : "—";
};

export const NotSentActions = ({
  loading,
  onSendLink,
}: {
  loading: boolean;
  onSendLink: () => void;
}) => (
  <>
    <p className={styles.validityNote}>
      <Clock className="h-3.5 w-3.5" />
      Link stays valid for 24 hours after it is sent.
    </p>
    <PrimaryButton disabled={loading} onClick={onSendLink}>
      <Send className="h-4 w-4" />
      {loading ? "Sending..." : "Send link to remitter"}
    </PrimaryButton>
  </>
);

export const LinkSentActions = ({
  sentAt,
  expiresAt,
  showResend,
  showCopy,
  copyLink,
  loading,
  proceedLabel,
  footerNote,
  onResendLink,
  onCopyLink,
  onProceed,
}: {
  sentAt?: string;
  expiresAt?: string;
  showResend: boolean;
  showCopy: boolean;
  copyLink: string;
  loading: boolean;
  proceedLabel: string;
  footerNote: string;
  onResendLink: () => void;
  onCopyLink: (link: string) => void;
  onProceed: () => void;
}) => (
  <>
    <Callout tone="success" title="Link delivered via SMS & Email">
      <div className={styles.metaRow}>
        <div>
          <p className={styles.metaLabel}>SENT</p>
          <p className={styles.metaValue}>{formatDateTime(sentAt)}</p>
        </div>
        <div>
          <p className={styles.metaLabel}>EXPIRES</p>
          <p className={styles.metaValue}>{formatExpiry(expiresAt)}</p>
        </div>
      </div>
    </Callout>

    {(showResend || showCopy) && (
      <div className={styles.textActions}>
        {showResend && (
          <button
            type="button"
            disabled={loading}
            onClick={onResendLink}
            className={styles.textAction}
          >
            <Send className="h-3.5 w-3.5" />
            {loading ? "Sending..." : "Resend"}
          </button>
        )}
        {showCopy && (
          <button
            type="button"
            disabled={!copyLink}
            onClick={() => onCopyLink(copyLink)}
            className={styles.textAction}
          >
            <Copy className="h-3.5 w-3.5" />
            Copy link
          </button>
        )}
      </div>
    )}

    <PrimaryButton onClick={onProceed}>
      <ArrowRight className="h-4 w-4" />
      {proceedLabel}
    </PrimaryButton>
    <p className={styles.footerNote}>{footerNote}</p>
  </>
);

export const InProgressActions = ({ onProceed }: { onProceed: () => void }) => (
  <>
    <Callout
      tone="info"
      title="KYC is still in progress"
      body="The remitter is completing verification. You can continue to remitter details."
    />
    <PrimaryButton onClick={onProceed}>
      <ArrowRight className="h-4 w-4" />
      Proceed to Remitter Details
    </PrimaryButton>
  </>
);

export const ApprovedActions = ({
  proceedLabel,
  onProceed,
}: {
  proceedLabel: string;
  onProceed: () => void;
}) => (
  <>
    <Callout tone="success" title="KYC approved" body="Continue with the remittance flow." />
    <PrimaryButton onClick={onProceed}>
      <ArrowRight className="h-4 w-4" />
      {proceedLabel}
    </PrimaryButton>
  </>
);

export const GenerateNewActions = ({
  title,
  body,
  loading,
  disabled,
  onGenerateNewLink,
}: {
  title: string;
  body: string;
  loading: boolean;
  disabled: boolean;
  onGenerateNewLink: () => void;
}) => (
  <>
    <Callout tone="warn" title={title} body={body} />
    <PrimaryButton disabled={disabled} onClick={onGenerateNewLink}>
      <RefreshCw className="h-4 w-4" />
      {loading ? "Generating..." : "Generate new link"}
    </PrimaryButton>
  </>
);
